import moment from "moment-timezone"

// The shipping address and contact person of a sheet, as CPanel writes and prefills them. Events owns
// the rules (the list of counties, the fields a courier needs); these helpers only present them.

export const countryNames = { RO: "Romania", BG: "Bulgaria", MD: "Moldova" }
// The country a club's location names, when the address rules know it.
export const countryCode = name => Object.keys(countryNames).find(code => countryNames[code] === name) || ""

export const blankAddress = { recipient: "", country: "", county: "", region: "", locality: "", street: "", number: "", postalCode: "", details: "" }

export const fieldNames = { recipient: "recipient", country: "country", county: "county", region: "region", locality: "locality", street: "street",
  number: "number", postalCode: "postal code", details: "details", name: "name", phone: "phone", email: "email" }
// "county, street, phone": fields still to complete.
export const fieldList = fields => fields.map(field => fieldNames[field] || field).join(", ")

// ["Str. Exemplului 12", "500036 Brașov, jud. Brașov, Romania"]: the street, then the place, by country.
export const addressLines = ({ street, number, postalCode, locality, county, region, country } = {}) => {
  const place = [[postalCode, locality].filter(Boolean).join(" "), country === "RO" ? county && `jud. ${county}` : region, countryNames[country] || country]
  return [[street, number].filter(Boolean).join(" "), place.filter(Boolean).join(", ")].filter(Boolean)
}

// "Silver Brașov Winter Cup, 02 Mar 2026": the event an address or contact was used at.
export const usedAtLabel = (usedAt, timezone) => [usedAt.name || "An event", usedAt.startDate && moment(usedAt.startDate).isValid()
  && moment(usedAt.startDate).tz(timezone || "Europe/Bucharest").format("DD MMM YYYY")].filter(Boolean).join(", ")

// Google writes a county as "Județul Brașov", "Brasov County" or "Bucharest"; the list as "Brașov".
const plain = value => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
  .replace(/\b(judetul|county|municipiul)\b/g, "").replace(/\s+/g, " ").trim()
const aliases = { bucharest: "bucuresti" }
export const alignCounty = (area, counties) => {
  const key = aliases[plain(area)] || plain(area)
  return (key && counties.find(county => plain(county) === key)) || ""
}

// The fields Google fills for a place in Romania, Bulgaria or Moldova: a Romanian county aligned to
// the list, a region elsewhere. What Google does not know stays empty, to complete.
export const addressFromGoogle = (result, counties) => {
  const part = type => result?.address_components?.find(item => item.types?.includes(type))?.long_name || ""
  const country = String(result?.address_components?.find(item => item.types?.includes("country"))?.short_name || "").toUpperCase()
  if (!countryNames[country]) throw new Error("Choose an address in Romania, Bulgaria or Moldova.")
  const area = part("administrative_area_level_1")
  return { country, county: country === "RO" ? alignCounty(area, counties) : "", region: country === "RO" ? "" : area,
    locality: part("locality") || part("postal_town") || part("administrative_area_level_2"), street: part("route"), number: part("street_number"),
    postalCode: part("postal_code") }
}

// Outside Romania a courier calls a number with its country prefix: a Romanian mobile number, 07…,
// becomes +40 7…, written from its digits unless it starts with them. Events applies the same rule;
// any other number without a prefix, free text included, stays as it is, to complete.
export const courierPhone = (phone, country) => {
  const value = String(phone || "").trim()
  if (!value || !country || country === "RO" || /^\+\d/.test(value)) return value
  const digits = value.replace(/[\s().\-/]/g, "")
  if (!/^07\d{8}$/.test(digits)) return value
  return `+40 ${value.startsWith("0") ? value.slice(1) : digits.slice(1)}`
}

// An email a courier can use; any other is saved as it is and stays to complete.
export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
